import React, { Suspense, useEffect, lazy, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient } from "react-query";
import { Loading } from "./aiapps/loading";
import { getLocales } from './core/utils/app';
import { SetCookie, GetCookie } from './core/utils/cookies';
import { aiproFlags } from './hooks/flags';
import i18n from './i18n';
import GlobalProvider from './core/context/GlobalProvider';

const queryClient = new QueryClient();

// Lazy-loaded components
const Home = lazy(() => import("./home"));
const Pricing = lazy(() => import("./pricing"));
const Upgrade = lazy(() => import("./upgrade"));
const UpgradePay = lazy(() => import("./pay-upgrade"));
const UpgradePayEnt = lazy(() => import("./pay-upgrade/index_ent"));
const Downgrade = lazy(() => import("./downgrade"));
const Register = lazy(() => import("./register"));
const Register9 = lazy(() => import("./register/flow09"));
const Login = lazy(() => import("./login"));
const Login9 = lazy(() => import("./login/flow09"));
const Forgot = lazy(() => import("./forgot"));
const Forgot9 = lazy(() => import("./forgot/flow09"));
const Reset = lazy(() => import("./resetpass"));
const Pay = lazy(() => import("./pay"));
const PayV1 = lazy(() => import("./pay/index_01"));
const PayV2 = lazy(() => import("./pay/index_02"));
const PayV3 = lazy(() => import("./pay/index_03"));
const PayV4 = lazy(() => import("./pay/index_04"));
const PayV5 = lazy(() => import("./pay/index_05"));
const PayV6 = lazy(() => import("./pay/index_05"));
const PayV7 = lazy(() => import("./pay/index_06"));
const PayFlow09 = lazy(() => import("./pay/flow_09"));
const PayFlow09Rec = lazy(() => import("./pay/flow_09_rec"));
const PayRef = lazy(() => import("./pay-reference/index"));
const PayConfirm = lazy(() => import("./pay-confirm/index"));
const Thankyou = lazy(() => import("./thankyou"));
const ThankYouFake = lazy(() => import("./thankyou/index_fake"));
const MyAccount = lazy(() => import("./my-account"));
const Manage = lazy(() => import("./manage-account"));
const ChangeCard = lazy(() => import("./change-card"));
const EmailTemplates = lazy(() => import("./emailtemplates"));
const ActiveSession = lazy(() => import("./active-session"));
const NotFoundPage = lazy(() => import("./404"));
const RedirectAccount = lazy(() => import("./redirect-account-required"));
const SplashPage = lazy(() => import("./splashscreen"));
// LPs
const StartStableDiffusion = lazy(() => import("./lp/start-stable-diffusion"));
const StartChatGPT = lazy(() => import("./lp/start-chat-gpt"));
const StartChatGPTgo = lazy(() => import("./lp/start-chatgpt-go"));
const StartChatGPTv2 = lazy(() => import("./lp/start-chatgpt-v2"));
const Text2Image = lazy(() => import("./lp/text-to-image"));
const Survey = lazy(() => import("./survey"));
const Resume = lazy(() => import("./resume-subscription"));
const Review = lazy(() => import("./review"));
const RegDsgnRegisterPage = lazy(() => import("./register/reg_dsgn/RegDsgnRegisterPage"));

function App() {
  const [existingCookie, setExistingCookie] = useState('');

  useEffect(() => {
    import("./tailwind.scss");
    import("./index.css");

    if(window.flags) {
      aiproFlags.flags = window.flags;
      for(var k in aiproFlags.flags) {
        if(aiproFlags.flags[k]){
          SetCookie(k, aiproFlags.flags[k],  { domain: '.ai-pro.org', path: '/' });
        }
      }
    }
    // Initial setup
    getLocales();
    if(aiproFlags.flags['mode'] === 'test') SetCookie("mode", aiproFlags.flags['mode'], {path: '/', domain: '.ai-pro.org'});
    setExistingCookie(GetCookie('locales'));

    let validLocales = [
      'en', 'ar', 'de', 'es', 'fr', 'it', 'pl', 'pt', 'sv', 'tr',
    ];

    if (existingCookie && validLocales.includes(existingCookie)) {
      // Only update if different from current language
        console.log("Changing language to:", existingCookie);
        i18n.changeLanguage(existingCookie);
    }
    // eslint-disable-next-line -- Run and set language once
  }, []);

  return (
    <Router>
      <div>
      <GlobalProvider
          queryClient={queryClient}
        >
          <Suspense fallback={<Loading />}>
            <div dir={(existingCookie === 'ar' ? "rtl" : "")} className="">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/home" element={<Home />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/pricing-tbl" element={<Pricing />} />
                <Route path="/pricing-plans" element={<Pricing />} />
                <Route path="/upgrade" element={<Upgrade />} />
                <Route path="/upgrade/:id" element={<UpgradePay />} />
                <Route path="/upgrade-ent/:member" element={<UpgradePayEnt />} />
                <Route path="/downgrade" element={<Downgrade />} />
                <Route path="/downgrade/:id" element={<UpgradePay />} />
                <Route path="/register" element={GetCookie("flow") === "09" ? <Register9 /> : <Register />} />
                <Route path="/register-auth" element={GetCookie("flow") === "09" ? <Register9 /> : <Register />} />
                <Route path="/registration" element={GetCookie("reg_dsgn") === "01" ? <RegDsgnRegisterPage /> : <Register9 />} />
                <Route path="/pay" element={<Pay />} />
                <Route path="/checkout-page-rec" element={<PayFlow09Rec />} />
                <Route path="/checkout-page-apple-google" element={<PayFlow09 />} />
                <Route path="/checkout-multi-lang" element={<PayFlow09 />} />
                <Route path="/pay/1LCXJMZNX6" element={<Pay />} />
                <Route path="/pay/bH0w05VJXk" element={<PayV1 />} />
                <Route path="/pay/VS6lni4hKx" element={<PayV2 />} />
                <Route path="/pay/uHTinVqsUl" element={<PayV3 />} />
                <Route path="/pay/mcWiDilmgQ" element={<PayV4 />} />
                <Route path="/pay/2FQH9T5Y7P" element={<PayV5/>} />
                <Route path="/pay/LZQ6N8V2A" element={<PayV6/>} />
                <Route path="/pay/FSQ6N8V2A" element={<PayV7/>} />
                <Route path="/payment-reference" element={<PayRef />} />
                <Route path="/payment-confirm" element={<PayConfirm />} />
                <Route path="/login" element={GetCookie("flow") === "09" ? <Login9 /> : <Login />} />
                <Route path="/forgot" element={GetCookie("flow") === "09" ? <Forgot9 /> : <Forgot />} />
                <Route path="/resetpass" element={<Reset />} />
                <Route path="/thankyou" element={<Thankyou />} />
                <Route path="/ty" element={<ThankYouFake />} />
                <Route path="/my-account" element={<MyAccount />} />
                <Route path="/manage-account" element={<Manage />} />
                <Route path="/change-card" element={<ChangeCard />} />
                <Route path="/emailtemplates" element={<EmailTemplates />} />
                <Route path="/resume" element={<Resume />} />
                <Route
                  path="/redirect-account-required"
                  element={<RedirectAccount />}
                />
                <Route
                  path="/start-stable-diffusion"
                  element={<StartStableDiffusion />}
                />
                <Route path="/start-chat-gpt" element={<StartChatGPT />} />
                <Route path="/start-chatgpt-go" element={<StartChatGPTgo />} />
                <Route path="/start-chatgpt-v2" element={<StartChatGPTv2 />} />
                <Route path="/text-to-image" element={<Text2Image />} />
                <Route path="/splash" element={<SplashPage />} />
                <Route path="/survey" element={<Survey />} />
                <Route path="/active-session" element={<ActiveSession />} />
                <Route path="/*" element={<NotFoundPage />} />
                <Route path="/review" element={<Review />} />
              </Routes>
            </div>
          </Suspense>
          </GlobalProvider>
        <div className="loader-container">
          <div className="overlay"></div>
          <div className="lds-ai">
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      </div>
    </Router>
  );
}
export default App;